import React, { Component, ComponentType, createRef, RefObject } from 'react';
import debounce from 'lodash/debounce';
import max from 'lodash/max';
import { logger } from '@origin-digital/reporting-client';

export interface IWithHeightAlignmentState {
  height?: number;
  rendered?: boolean;
}

/**
 * Returns a styled component with height applied w.r.p alignmentReferenceClass
 * @param elementClass : for finding all the elements which the height needs to be adjusted
 * @param parentClass : scope the dom selection to parent class
 * @param applyMaxHeight : apply max height or not
 */
export const alignElementHeights =
  (elementClass: string, parentClass?: string, style?: object, applyMaxHeight = true) =>
  <P extends object>(WrappedComponent: ComponentType<P>) =>
    class WithHeightAlignment extends Component<P, IWithHeightAlignmentState> {
      public ref: RefObject<HTMLDivElement | null>;
      public debouncedHandler: (() => void) | null;
      public observer: MutationObserver | null;

      constructor(props: P) {
        super(props);
        this.state = {
          height: 0,
          rendered: false,
        };
        this.ref = createRef();
        this.debouncedHandler = null;
        this.observer = null;
      }

      get eventName() {
        return `parent:${parentClass},element:${elementClass}`;
      }

      componentDidMount() {
        this.debouncedHandler = debounce(this.alignElementHeight, 100);
        window.addEventListener(this.eventName, this.debouncedHandler);
        window.addEventListener('resize', this.debouncedHandler);

        if (!this.state.rendered) {
          this.setState(
            {
              height: getMaxElementHeight(elementClass, parentClass),
              rendered: true,
            },
            () => {
              this.dispatchEvent();
              this.setupObserver();
            }
          );
        }
      }

      componentWillUnmount() {
        if (this.debouncedHandler) {
          window.removeEventListener('resize', this.debouncedHandler);
          window.removeEventListener(this.eventName, this.debouncedHandler);
        }
        if (this.observer) {
          this.observer.disconnect();
        }
      }

      dispatchEvent = () => {
        window.dispatchEvent(
          new CustomEvent(this.eventName, {
            bubbles: true,
            cancelable: false,
            detail: null,
          })
        );
      };

      alignElementHeight = () => {
        const height = getMaxElementHeight(elementClass, parentClass);
        if (height !== this.state.height) {
          this.setState({ height });
        }
      };

      setupObserver = () => {
        try {
          const node = this.ref.current;
          if (node) {
            const debouncedDispatcher = debounce(this.dispatchEvent, 100);
            this.observer = new MutationObserver(debouncedDispatcher);
            this.observer.observe(node, {
              attributes: true,
              childList: true,
              subtree: true,
            });
          }
        } catch (error) {
          logger.error('Failed to set up observer:', error);
        }
      };

      render() {
        const { rendered, height } = this.state;
        const integerHeight = +(height ?? 0);

        let wrapperStyle: any = style || {};
        wrapperStyle = {
          width: '100%',
          ...wrapperStyle,
        };

        if (rendered && integerHeight > 0) {
          wrapperStyle.height = applyMaxHeight ? `${integerHeight}px` : `auto`;
        }

        return (
          <div
            className="AlignmentElementHeights__AlignmentWrapper"
            data-height={applyMaxHeight ? integerHeight : 'auto'}
            style={wrapperStyle}
            ref={this.ref}
          >
            <WrappedComponent {...(this.props as P)} />
          </div>
        );
      }
    };

// Polyfill CustomEvent for IE11
// See: https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent#Polyfill

(function () {
  if (typeof window.CustomEvent === 'function') return;

  function CustomEvent(event: string, params?: CustomEventInit): CustomEvent {
    params = params || { bubbles: false, cancelable: false, detail: null };
    const evt = document.createEvent('CustomEvent');
    evt.initCustomEvent(event, params.bubbles || false, params.cancelable || false, params.detail);
    return evt;
  }

  (window as any).CustomEvent = CustomEvent;
})();

const getMaxElementHeight = (elementClass: string, parentClass?: string): number => {
  const selector = parentClass ? `.${parentClass} .${elementClass}` : `.${elementClass}`;
  const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
  const heights = elements.map((e: HTMLElement) => e.offsetHeight);
  const maxHeight = max(heights);
  return maxHeight ?? 0;
};
